import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";

const PHASE_WEIGHTS = [22, 56, 84, 100];

export default function AnalyzingState({ active }) {
  const { t } = useLang();
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!active) {
      setPhase(0);
      setProgress(0);
      return;
    }
    let cancelled = false;
    let target = 0;
    let p = 0;

    const phaseInterval = setInterval(() => {
      setPhase((prev) => {
        const next = Math.min(prev + 1, t.analyzing.phases.length - 1);
        target = PHASE_WEIGHTS[next];
        return next;
      });
    }, 1600);

    const progressInterval = setInterval(() => {
      if (cancelled) return;
      const t0 = PHASE_WEIGHTS[phase] || target || 22;
      p = Math.min(p + (t0 - p) * 0.12 + 0.4, 98);
      setProgress(p);
    }, 90);

    return () => {
      cancelled = true;
      clearInterval(phaseInterval);
      clearInterval(progressInterval);
    };
  }, [active, phase, t.analyzing.phases.length]);

  if (!active) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-white/85 backdrop-blur-xl"
      data-testid="analyzing-overlay"
    >
      <div className="container-x flex max-w-2xl flex-col items-center text-center">
        <span className="overline" data-testid="analyzing-overline">
          {t.analyzing.title}
        </span>
        <motion.h2
          className="mt-4 text-3xl text-zinc-900 md:text-5xl"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {t.analyzing.phases[phase]}
        </motion.h2>

        <div className="mt-10 w-full max-w-md">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
            <motion.div
              className="h-full rounded-full bg-[color:var(--sentinel)]"
              style={{ width: `${progress}%` }}
              transition={{ ease: "easeOut" }}
            />
          </div>
          <p className="mt-3 text-xs uppercase tracking-[0.22em] text-zinc-400">
            {Math.round(progress)}%
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
          {t.analyzing.phases.map((p, i) => (
            <div
              key={i}
              className={`rounded-xl border px-3 py-2 text-xs transition ${
                i <= phase
                  ? "border-[color:var(--sentinel)] bg-[color:var(--sentinel-tint)] text-[color:var(--sentinel-darker)]"
                  : "border-zinc-200 bg-white text-zinc-400"
              }`}
              data-testid={`analyzing-phase-${i}`}
            >
              {p.replace("…", "")}
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-zinc-500">{t.analyzing.note}</p>
      </div>
    </motion.div>
  );
}
